import { User03 } from "@untitled-ui/icons-react";
import classNames from "classnames";
import { type ImgHTMLAttributes, type ReactNode } from "react";

import { type Prettify, type User } from "@/types";

import Tooltip from "../helpers/Tooltip";
import ImageWithFallback from "./ImageWithFallback";

export type UserAvatarProps = {
  user?: Partial<Prettify<User>>;
  size?: keyof typeof sizes;
  shadow?: boolean;
  isOnline?: boolean;
  fallBackIcon?: ReactNode;
  disableTooltip?: boolean;
} & ImgHTMLAttributes<HTMLImageElement>;

const sizes = {
  xs: "size-6",
  sm: "size-8",
  md: "size-10",
  lg: "size-12",
  xl: "size-14",
  "2xl": "size-16",
  "3xl": "size-24",
  "4xl": "size-32",
  "5xl": "size-40",
};

const iconSizes = {
  xs: "size-4",
  sm: "size-5",
  md: "size-6",
  lg: "size-7",
  xl: "size-8",
  "2xl": "size-9",
  "3xl": "size-10",
  "4xl": "size-11",
  "5xl": "size-12",
};

const onlineSizes = {
  xs: "size-1.5",
  sm: "size-2",
  md: "size-2.5",
  lg: "size-3",
  xl: "size-3.5",
  "2xl": "size-4",
  "3xl": "size-5",
  "4xl": "size-6",
  "5xl": "size-7",
};

export default function UserAvatar({
  user,
  size = "md",
  className,
  shadow = false,
  isOnline = false,
  fallBackIcon,
  disableTooltip = false,
  ...props
}: UserAvatarProps) {
  return (
    <div className={classNames("relative inline-block", sizes[size])}>
      <Tooltip
        content={`${user?.first_name} ${user?.last_name}`}
        disabled={disableTooltip}
      >
        <div
          className={classNames(
            "relative rounded-full",
            shadow && "shadow-lg",
            sizes[size],
          )}
        >
          <ImageWithFallback
            src={user?.profile_picture_thumb}
            alt="user image"
            tabIndex={0}
            className={classNames(
              "focus:ring-primary-100 rounded-full object-cover focus:ring-4 focus:outline-hidden",
              shadow && "border-4 border-white",
              sizes[size],
              className,
            )}
            fallback={
              <div
                tabIndex={0}
                className={classNames(
                  "focus:ring-primary-100 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 focus:ring-4 focus:outline-hidden",
                  sizes[size],
                  className,
                )}
              >
                {fallBackIcon || (
                  <User03
                    className={classNames("text-gray-500", iconSizes[size])}
                  />
                )}
              </div>
            }
            {...props}
          />
          {isOnline && (
            <div
              className={classNames(
                "border-1.5 bg-success-500 absolute end-0 bottom-0 rounded-full border-white",
                onlineSizes[size],
              )}
            />
          )}
        </div>
      </Tooltip>
    </div>
  );
}
